// Goal landing pages: one entry per investor goal. Each drives
// pages/GoalPage.jsx (calculator, gated report, gated e-book, FAQ).
// Phase 1: retirement, child-education, first-crore. Phase 2: tax-saving,
// regular-income, dream-home, emergency-fund. Phase 3: child-marriage,
// women-wealth, nri-investing, business-treasury, promoter-wealth.
import {
  goalPlan,
  retirementPlan,
  timeToTarget,
  costOfDelay,
  emi,
  elssTaxPlan,
  emergencyFund,
  swpWithInflation,
  idleCashEarnings,
  sipFutureValue,
  fmtCompact,
  fmtINR,
} from "@/lib/finance";

const yrs = (v) => `${v} yrs`;
const pct = (v) => `${v}%`;

// Defaults agreed with RIS: equity 11%, debt 7%, inflation 6%. Visitors can change them.
export const DEFAULTS = { equityReturn: 11, debtReturn: 7, inflation: 6 };

export const GOALS = [
  {
    slug: "retirement",
    name: "Retirement",
    audience: "individual",
    seo: {
      title: "Retirement Planning Calculator & Free Guide | Rao Investment Solutions",
      description:
        "Estimate the retirement corpus you may need and the monthly SIP to build it. Download the free Retirement Allocation Playbook.",
    },
    hero: {
      label: "Goal · Retirement",
      title: "Retire on your terms,",
      accent: "not on a guess.",
      intro:
        "Find out what your retirement may really cost, how much to invest each month, and how your mix of assets should shift as the date comes closer.",
    },
    inputs: [
      { key: "age", label: "Your age today", min: 22, max: 60, step: 1, default: 32, format: yrs },
      { key: "retireAge", label: "Retire at", min: 45, max: 70, step: 1, default: 60, format: yrs },
      { key: "expense", label: "Monthly expenses today", min: 20000, max: 500000, step: 5000, default: 60000, format: fmtCompact },
      { key: "existing", label: "Retirement savings so far", min: 0, max: 20000000, step: 100000, default: 500000, format: fmtCompact },
      { key: "inflation", label: "Inflation", min: 3, max: 10, step: 0.5, default: DEFAULTS.inflation, format: pct },
      { key: "ret", label: "Return before retirement", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
      { key: "postRet", label: "Return after retirement", min: 4, max: 10, step: 0.5, default: DEFAULTS.debtReturn, format: pct },
    ],
    compute: (v) => {
      const r = retirementPlan({
        currentAge: v.age,
        retireAge: Math.max(v.retireAge, v.age + 1),
        lifeExpectancy: 85,
        monthlyExpense: v.expense,
        inflation: v.inflation,
        preReturn: v.ret,
        postReturn: v.postRet,
        currentInvestments: v.existing,
      });
      const g = goalPlan({ cost: r.shortfall, years: r.yearsToRetire, inflation: 0, annualReturn: v.ret });
      return {
        yearsLeft: r.yearsToRetire,
        headline: { label: "Estimated monthly SIP needed", value: fmtINR(r.requiredSip) },
        stats: [
          { label: "Corpus needed at retirement", value: fmtCompact(r.requiredCorpus) },
          { label: "Monthly expense at retirement", value: fmtINR(r.futureMonthly) },
          { label: "Your savings may grow to", value: fmtCompact(r.fvExisting) },
          { label: "Or start a 10% step-up SIP at", value: fmtINR(g.stepSip) },
        ],
        series: g.series.map((s) => ({ ...s, value: s.value + Math.round(v.existing * Math.pow(1 + v.ret / 100, s.year)) })),
        delay: costOfDelay({ target: r.shortfall, years: r.yearsToRetire, annualReturn: v.ret }),
        summary: `Retire at ${v.retireAge}: corpus ${fmtCompact(r.requiredCorpus)}, SIP ${fmtINR(r.requiredSip)}/month`,
      };
    },
    ebook: {
      title: "The Retirement Allocation Playbook",
      subtitle: "How to build, protect and draw down a retirement corpus",
      file: "/ebooks/retirement-allocation-playbook.pdf",
      cover: "/ebooks/retirement-allocation-playbook.png",
      chapters: [
        "What retirement really costs: inflation over 25+ years",
        "The three phases: accumulate, consolidate, draw down",
        "How your equity–debt–gold mix should shift with age",
        "SWP: turning a corpus into a monthly income",
        "Seven retirement mistakes we see most often",
        "Your one-page retirement checklist",
      ],
    },
    faq: [
      { q: "How much corpus do I need to retire?", a: "It depends on your expenses, the years until retirement, inflation and how long the corpus must last. The calculator above gives an illustrative estimate; a discovery call refines it with your actual cash flows." },
      { q: "Is it too late to start at 45?", a: "No, but the monthly amount needed rises sharply with every year of delay. A higher SIP, a step-up plan and a slightly later retirement date are the usual levers." },
      { q: "Should my retirement money be in equity?", a: "In the early years, a meaningful equity share usually helps beat inflation. As retirement approaches, the mix typically shifts towards debt and bonds to protect what has been built." },
      { q: "How will I get a monthly income after retiring?", a: "A Systematic Withdrawal Plan (SWP) from a debt-heavy portfolio is a common approach, sometimes combined with bonds for regular interest." },
      { q: "Are these returns guaranteed?", a: "No. All figures are illustrative estimates based on the assumptions you choose. Mutual fund investments are subject to market risks." },
    ],
  },
  {
    slug: "child-education",
    name: "Child's Education",
    audience: "individual",
    seo: {
      title: "Child Education Planning Calculator & Free Guide | Rao Investment Solutions",
      description:
        "Estimate the future cost of your child's education in India or abroad and the monthly SIP to fund it. Download the free guide.",
    },
    hero: {
      label: "Goal · Child's Education",
      title: "Their dream college,",
      accent: "funded on time.",
      intro:
        "Education costs rise faster than most prices. See what your child's degree may cost when they need it, and the monthly plan that can get you there.",
    },
    inputs: [
      { key: "cost", label: "Cost of the course today", min: 500000, max: 10000000, step: 100000, default: 2500000, format: fmtCompact },
      { key: "years", label: "Years until college", min: 1, max: 20, step: 1, default: 12, format: yrs },
      { key: "existing", label: "Saved for this so far", min: 0, max: 5000000, step: 50000, default: 200000, format: fmtCompact },
      { key: "inflation", label: "Education inflation", min: 4, max: 12, step: 0.5, default: 8, format: pct },
      { key: "ret", label: "Assumed annual return", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      const g = goalPlan({ cost: v.cost, years: v.years, inflation: v.inflation, annualReturn: v.ret, existing: v.existing });
      return {
        yearsLeft: v.years,
        headline: { label: "Estimated monthly SIP needed", value: fmtINR(g.sip) },
        stats: [
          { label: "Cost when your child needs it", value: fmtCompact(g.futureCost) },
          { label: "Your savings may grow to", value: fmtCompact(g.fvExisting) },
          { label: "Gap to fund", value: fmtCompact(g.gap) },
          { label: "Or start a 10% step-up SIP at", value: fmtINR(g.stepSip) },
        ],
        series: g.series,
        delay: costOfDelay({ target: g.gap, years: v.years, annualReturn: v.ret }),
        summary: `Education in ${v.years} yrs: cost ${fmtCompact(g.futureCost)}, SIP ${fmtINR(g.sip)}/month`,
      };
    },
    ebook: {
      title: "Funding Your Child's Education",
      subtitle: "A parent's plan for India and overseas degrees",
      file: "/ebooks/funding-childs-education.pdf",
      cover: "/ebooks/funding-childs-education.png",
      chapters: [
        "Why education inflation runs ahead of everything else",
        "What degrees may cost: India vs abroad",
        "Building the fund: SIP, step-up and lumpsum",
        "De-risking in the last three years",
        "Loans, scholarships and insurance as back-ups",
        "Your education-goal checklist",
      ],
    },
    faq: [
      { q: "What inflation should I assume for education?", a: "Education costs in India have historically risen faster than general inflation. We default to 8% for illustration; overseas costs also depend on currency movements." },
      { q: "When should I start?", a: "As early as possible. With 15 years to go, compounding does much of the work; with 5 years, most of the money has to come from your own contributions." },
      { q: "Should I use a child plan from an insurer?", a: "Keep protection and investment separate in most cases: adequate term cover for the parent, and a goal-based portfolio for the education fund." },
      { q: "What happens as admission gets close?", a: "In the final 2–3 years the fund is typically moved progressively into debt so that a market fall does not hit the fees." },
      { q: "Are these returns guaranteed?", a: "No. All figures are illustrative estimates. Mutual fund investments are subject to market risks." },
    ],
  },
  {
    slug: "first-crore",
    name: "Your First ₹1 Crore",
    audience: "individual",
    seo: {
      title: "₹1 Crore SIP Calculator & Free Guide | Rao Investment Solutions",
      description:
        "See how long it may take to build ₹1 crore with a SIP and step-ups. Download the free guide, Your Path to ₹1 Crore.",
    },
    hero: {
      label: "Goal · Wealth Creation",
      title: "Your first ₹1 crore",
      accent: "is a plan, not luck.",
      intro:
        "See how a monthly SIP, a yearly step-up and the right asset mix can compound towards your first crore, and how much sooner you may get there by starting today.",
    },
    inputs: [
      { key: "target", label: "Target corpus", min: 2500000, max: 100000000, step: 2500000, default: 10000000, format: fmtCompact },
      { key: "sip", label: "Monthly SIP", min: 1000, max: 300000, step: 1000, default: 20000, format: fmtINR },
      { key: "step", label: "Yearly step-up", min: 0, max: 25, step: 1, default: 10, format: pct },
      { key: "existing", label: "Existing investments", min: 0, max: 10000000, step: 50000, default: 0, format: fmtCompact },
      { key: "ret", label: "Assumed annual return", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      const t = timeToTarget({ target: v.target, sip: v.sip, annualReturn: v.ret, stepPct: v.step, existing: v.existing });
      const flat = timeToTarget({ target: v.target, sip: v.sip, annualReturn: v.ret, stepPct: 0, existing: v.existing });
      const years = t.months ? t.months / 12 : 50;
      const label = (m) => (m ? `${Math.floor(m / 12)} yrs ${m % 12} mo` : "50+ yrs");
      return {
        yearsLeft: Math.ceil(years),
        headline: { label: `Estimated time to ${fmtCompact(v.target)}`, value: label(t.months) },
        stats: [
          { label: "Without step-up", value: label(flat.months) },
          { label: "Total you invest", value: fmtCompact(t.invested) },
          { label: "Estimated growth", value: fmtCompact(Math.max(0, t.corpus - t.invested)) },
          { label: "Time saved by step-up", value: t.months && flat.months ? label(flat.months - t.months) : "—" },
        ],
        series: t.series,
        delay: costOfDelay({ target: v.target, years: Math.max(6, Math.ceil(years)), annualReturn: v.ret }),
        summary: `${fmtCompact(v.target)} goal: ${label(t.months)} with ${fmtINR(v.sip)} SIP, ${v.step}% step-up`,
      };
    },
    ebook: {
      title: "Your Path to ₹1 Crore",
      subtitle: "The habits, maths and asset mix behind a first crore",
      file: "/ebooks/path-to-1-crore.pdf",
      cover: "/ebooks/path-to-1-crore.png",
      chapters: [
        "The maths of compounding, in plain numbers",
        "Why the step-up matters more than the starting SIP",
        "Choosing the asset mix for a 10–15 year goal",
        "Staying invested through market falls",
        "Rebalancing: when and why",
        "Your ₹1 crore action plan",
      ],
    },
    faq: [
      { q: "How much SIP do I need for ₹1 crore?", a: "It depends on the time you have and the return assumed. At an illustrative 11%, a ₹20,000 SIP with a 10% yearly step-up may reach ₹1 crore in about 12–13 years." },
      { q: "Why is step-up so powerful?", a: "Raising your SIP in line with your income means later contributions are larger and the target is reached years earlier." },
      { q: "Which funds should I choose?", a: "That depends on your risk profile and horizon. We build an allocation first and then select funds to fill it." },
      { q: "What if the market falls?", a: "Falls are normal on a 10+ year journey. A SIP keeps buying through them; stopping is what usually hurts long-term outcomes." },
      { q: "Are these returns guaranteed?", a: "No. All figures are illustrative estimates. Mutual fund investments are subject to market risks." },
    ],
  },
  {
    slug: "tax-saving",
    name: "Tax Saving (ELSS)",
    audience: "individual",
    seo: {
      title: "ELSS Tax Saving Calculator & Free Guide | Rao Investment Solutions",
      description:
        "See how much tax you may save under Section 80C with ELSS, and what the investment could grow to. Download the free guide, Smart Tax Saving with ELSS.",
    },
    hero: {
      label: "Goal · Tax Saving",
      title: "Save tax this year,",
      accent: "build wealth for years.",
      intro:
        "ELSS funds can lower your tax under Section 80C (old regime) and give your money a chance to grow, with the shortest lock-in among 80C options at three years.",
    },
    inputs: [
      { key: "annual", label: "Yearly ELSS investment", min: 12000, max: 300000, step: 6000, default: 150000, format: fmtINR },
      { key: "slab", label: "Your income-tax slab", min: 5, max: 30, step: 5, default: 30, format: pct },
      { key: "years", label: "Years you invest", min: 3, max: 25, step: 1, default: 10, format: yrs },
      { key: "ret", label: "Assumed annual return", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      const t = elssTaxPlan({ annual: v.annual, slabPct: v.slab, years: v.years, annualReturn: v.ret });
      return {
        yearsLeft: v.years,
        headline: { label: "Estimated tax saved every year", value: fmtINR(t.taxSavedYear) },
        stats: [
          { label: `Tax saved over ${v.years} yrs`, value: fmtCompact(t.taxSavedTotal) },
          { label: "Deduction used (max ₹1.5 L)", value: fmtINR(t.deduction) },
          { label: "Total invested", value: fmtCompact(t.invested) },
          { label: "Investment may grow to", value: fmtCompact(t.fv) },
        ],
        series: t.series,
        compare: {
          title: "Each year's investment vs tax saved",
          intro: "How much of your yearly ELSS investment comes back as lower tax.",
          rows: [
            { label: "You invest", amount: v.annual, highlight: true },
            { label: "Tax you may save", amount: t.taxSavedYear },
            { label: "Your effective cost", amount: v.annual - t.taxSavedYear },
          ],
        },
        allocationNote:
          "ELSS is an equity fund, so it suits money you can leave for at least three years. Within your overall plan it is one part of the equity allocation, not the whole of it.",
        summary: `ELSS ${fmtINR(v.annual)}/yr at ${v.slab}% slab: tax saved ${fmtINR(t.taxSavedYear)}/yr`,
      };
    },
    ebook: {
      title: "Smart Tax Saving with ELSS",
      subtitle: "Section 80C, made simple and made to grow",
      file: "/ebooks/smart-tax-saving-elss.pdf",
      cover: "/ebooks/smart-tax-saving-elss.png",
      chapters: [
        "How Section 80C works, and the old vs new regime",
        "ELSS vs PPF, FD and insurance for tax saving",
        "SIP or lumpsum: avoiding the March rush",
        "The three-year lock-in, explained",
        "How ELSS fits into your overall asset mix",
        "Your tax-saving checklist",
      ],
    },
    faq: [
      { q: "Can I claim ELSS under the new tax regime?", a: "No. The Section 80C deduction is available only if you opt for the old tax regime. Compare both regimes for your income before deciding." },
      { q: "What is the lock-in for ELSS?", a: "Three years from the date of each investment. With a SIP, every instalment has its own three-year lock-in." },
      { q: "Is ELSS better than PPF?", a: "They do different jobs. PPF is a long, fixed-rate government scheme; ELSS is an equity fund with market-linked returns and higher volatility. Many investors use both." },
      { q: "Should I invest in ELSS through SIP?", a: "A monthly SIP spreads your investment across the year and avoids a last-minute lumpsum in March." },
      { q: "Are ELSS returns guaranteed?", a: "No. ELSS invests in equity and returns are market-linked. All figures here are illustrative. Tax laws can change; consult a tax adviser for your situation." },
    ],
  },
  {
    slug: "regular-income",
    name: "Regular Income (SWP)",
    audience: "individual",
    seo: {
      title: "SWP Calculator: How Long Will My Corpus Last? | Rao Investment Solutions",
      description:
        "Estimate how long your corpus may last with a monthly withdrawal that rises with inflation. Download the free guide, Living Off Your Corpus.",
    },
    hero: {
      label: "Goal · Regular Income",
      title: "A monthly income",
      accent: "that keeps up with life.",
      intro:
        "A Systematic Withdrawal Plan pays you every month while the rest of your money stays invested. See how long your corpus may last, and how to make it last longer.",
    },
    inputs: [
      { key: "corpus", label: "Your corpus", min: 1000000, max: 100000000, step: 500000, default: 10000000, format: fmtCompact },
      { key: "withdrawal", label: "Monthly withdrawal (today)", min: 10000, max: 500000, step: 5000, default: 50000, format: fmtINR },
      { key: "inflation", label: "Yearly increase in withdrawal", min: 0, max: 10, step: 0.5, default: DEFAULTS.inflation, format: pct },
      { key: "ret", label: "Assumed annual return", min: 4, max: 12, step: 0.5, default: DEFAULTS.debtReturn, format: pct },
    ],
    compute: (v) => {
      const plan = (w, infl) => swpWithInflation({ corpus: v.corpus, annualReturn: v.ret, withdrawal: w, inflation: infl });
      const r = plan(v.withdrawal, v.inflation);
      const flat = plan(v.withdrawal, 0);
      const lighter = plan(v.withdrawal * 0.85, v.inflation);
      const label = (m) => (m ? `${Math.floor(m / 12)} yrs ${m % 12} mo` : "50+ yrs");
      const yrsOf = (m) => (m ? m / 12 : 50);
      return {
        yearsLeft: 0,
        headline: { label: "Your corpus may last", value: label(r.months) },
        stats: [
          { label: "Withdrawal rate, year 1", value: `${(((v.withdrawal * 12) / v.corpus) * 100).toFixed(1)}%` },
          { label: "Total you may withdraw", value: fmtCompact(r.withdrawn) },
          { label: "Without yearly increases", value: label(flat.months) },
          { label: "If you withdraw 15% less", value: label(lighter.months) },
        ],
        series: r.series,
        chartLabels: { title: "Corpus over time", value: "Corpus left", invested: "Total withdrawn" },
        compare: {
          title: "How long it may last",
          intro: "Years the corpus may last under different withdrawal choices.",
          rows: [
            { label: "Your plan", amount: yrsOf(r.months), display: label(r.months), highlight: true },
            { label: "Withdraw 15% less", amount: yrsOf(lighter.months), display: label(lighter.months) },
            { label: "No yearly increase", amount: yrsOf(flat.months), display: label(flat.months) },
          ],
        },
        allocation: { equity: 25, debt: 65, gold: 10 },
        allocationNote:
          "For income, most of the corpus usually sits in debt and bonds for stability, with a smaller equity share to help it keep pace with inflation over a long retirement.",
        summary: `SWP ${fmtINR(v.withdrawal)}/mo from ${fmtCompact(v.corpus)}: lasts ${label(r.months)}`,
      };
    },
    ebook: {
      title: "Living Off Your Corpus",
      subtitle: "A practical guide to SWP and regular income",
      file: "/ebooks/living-off-your-corpus-swp.pdf",
      cover: "/ebooks/living-off-your-corpus-swp.png",
      chapters: [
        "What an SWP is, and how it differs from interest income",
        "The withdrawal rate: the number that decides everything",
        "Building an income portfolio: debt, bonds and equity",
        "The bucket approach: near, medium and long-term money",
        "Tax on SWP vs fixed-deposit interest",
        "Your regular-income checklist",
      ],
    },
    faq: [
      { q: "What is a safe withdrawal rate?", a: "There is no universally safe number; it depends on returns, inflation and how long the money must last. Lower withdrawal rates give the corpus a better chance of lasting." },
      { q: "Is SWP better than a fixed deposit?", a: "An SWP can be more tax-efficient because only the gains portion of each withdrawal is taxed, while FD interest is taxed in full. But mutual fund values fluctuate, unlike an FD." },
      { q: "Should my SWP corpus be in equity?", a: "Usually mostly in debt and bonds, with a smaller equity share to fight inflation. The right mix depends on your age and other income." },
      { q: "Can I change the withdrawal amount later?", a: "Yes. An SWP can be increased, reduced or paused. Reviewing it every year is sensible." },
      { q: "Is this income guaranteed?", a: "No. Mutual fund values and returns are market-linked. All figures here are illustrative estimates." },
    ],
  },
  {
    slug: "dream-home",
    name: "Dream Home",
    audience: "individual",
    seo: {
      title: "Home Down-Payment & EMI Planner | Rao Investment Solutions",
      description:
        "Plan the down payment for your home, see the monthly SIP to build it and the EMI you may pay. Download the free home-buyer's guide.",
    },
    hero: {
      label: "Goal · Dream Home",
      title: "The keys to your home,",
      accent: "without the strain.",
      intro:
        "Plan the down payment, see what the EMI may look like, and decide how much to save before you buy, so the home stays a joy and not a burden.",
    },
    inputs: [
      { key: "price", label: "Home price today", min: 2000000, max: 50000000, step: 500000, default: 8000000, format: fmtCompact },
      { key: "down", label: "Down payment", min: 10, max: 60, step: 5, default: 25, format: pct },
      { key: "years", label: "Years until you buy", min: 1, max: 15, step: 1, default: 5, format: yrs },
      { key: "existing", label: "Saved for this so far", min: 0, max: 10000000, step: 100000, default: 500000, format: fmtCompact },
      { key: "inflation", label: "Property price growth", min: 0, max: 10, step: 0.5, default: DEFAULTS.inflation, format: pct },
      { key: "ret", label: "Assumed annual return", min: 5, max: 13, step: 0.5, default: 9, format: pct },
      { key: "loanRate", label: "Home-loan interest rate", min: 6, max: 12, step: 0.25, default: 8.5, format: pct },
    ],
    compute: (v) => {
      const futurePrice = v.price * Math.pow(1 + v.inflation / 100, v.years);
      const downAmt = (futurePrice * v.down) / 100;
      const g = goalPlan({ cost: downAmt, years: v.years, inflation: 0, annualReturn: v.ret, existing: v.existing });
      const loan = futurePrice - downAmt;
      const emiFor = (yr) => emi(loan, v.loanRate, yr);
      return {
        yearsLeft: v.years,
        headline: { label: "Estimated monthly SIP for the down payment", value: fmtINR(g.sip) },
        stats: [
          { label: "Home price when you buy", value: fmtCompact(futurePrice) },
          { label: "Down payment needed", value: fmtCompact(downAmt) },
          { label: "Home loan amount", value: fmtCompact(loan) },
          { label: "EMI over 20 yrs (approx.)", value: fmtINR(emiFor(20)) },
        ],
        series: g.series,
        compare: {
          title: "EMI by loan tenure",
          intro: `Approximate EMI on a ${fmtCompact(loan)} loan at ${v.loanRate}%.`,
          rows: [
            { label: "15 years", amount: emiFor(15) },
            { label: "20 years", amount: emiFor(20), highlight: true },
            { label: "25 years", amount: emiFor(25) },
          ],
        },
        summary: `Home ${fmtCompact(futurePrice)} in ${v.years} yrs: down payment ${fmtCompact(downAmt)}, SIP ${fmtINR(g.sip)}/mo, EMI ~${fmtINR(emiFor(20))}`,
      };
    },
    ebook: {
      title: "Rent, Buy or Invest?",
      subtitle: "A home-buyer's guide to the biggest purchase of your life",
      file: "/ebooks/rent-buy-or-invest.pdf",
      cover: "/ebooks/rent-buy-or-invest.png",
      chapters: [
        "The real cost of owning a home",
        "How much home can you afford?",
        "Building the down payment: where to keep it",
        "Loan tenure: shorter vs longer",
        "Prepaying the loan vs investing the surplus",
        "Your home-buying checklist",
      ],
    },
    faq: [
      { q: "How much down payment should I plan for?", a: "Lenders typically fund 75–90% of the property value, so plan for at least 10–25%, plus registration, stamp duty and interiors, which are often not covered by the loan." },
      { q: "Where should I keep my down-payment savings?", a: "It depends on how soon you buy. For a 1–3 year horizon the money is usually kept mostly in debt; for longer horizons some equity may be appropriate." },
      { q: "What EMI can I afford?", a: "A common guideline is to keep all EMIs within about 35–40% of take-home income, so other goals are not crowded out." },
      { q: "Should I prepay my home loan or invest?", a: "It depends on your loan rate, tax position and risk appetite. We can compare both for your numbers." },
      { q: "Are these figures guaranteed?", a: "No. Property prices, loan rates and investment returns all vary. All figures here are illustrative estimates." },
    ],
  },
  {
    slug: "emergency-fund",
    name: "Emergency Fund",
    audience: "individual",
    seo: {
      title: "Emergency Fund Calculator & Checklist | Rao Investment Solutions",
      description:
        "Work out how big your emergency fund should be, how fast you can build it and where to keep it. Download the free Emergency Fund Checklist.",
    },
    hero: {
      label: "Goal · Emergency Fund",
      title: "The fund that protects",
      accent: "every other goal.",
      intro:
        "A job loss, a hospital bill or an urgent repair should not force you to break your long-term investments. Work out the cushion you need and how to build it.",
    },
    inputs: [
      { key: "expense", label: "Monthly expenses (incl. EMIs)", min: 10000, max: 500000, step: 5000, default: 60000, format: fmtINR },
      { key: "months", label: "Months of cover", min: 3, max: 12, step: 1, default: 6, format: (x) => `${x} months` },
      { key: "existing", label: "Already set aside", min: 0, max: 5000000, step: 10000, default: 100000, format: fmtCompact },
      { key: "saving", label: "You can add each month", min: 0, max: 200000, step: 1000, default: 20000, format: fmtINR },
    ],
    compute: (v) => {
      const e = emergencyFund({ monthlyExpense: v.expense, months: v.months, existing: v.existing, monthlySaving: v.saving });
      const when = e.monthsToBuild === 0 ? "Already there" : e.monthsToBuild ? `${e.monthsToBuild} months` : "Add a monthly amount";
      return {
        yearsLeft: 0,
        headline: { label: "Your emergency fund target", value: fmtINR(e.target) },
        stats: [
          { label: "Still to build", value: fmtINR(e.gap) },
          { label: "Time to build it", value: when },
          { label: "Keep in savings account (~1 month)", value: fmtINR(v.expense) },
          { label: "Keep in liquid / short-term debt", value: fmtINR(Math.max(0, e.target - v.expense)) },
        ],
        series: [],
        compare: {
          title: "How much cover is right?",
          intro: "Fund size for different levels of cover at your expenses.",
          rows: [3, 6, 9, 12].map((m) => ({ label: `${m} months`, amount: v.expense * m, highlight: m === v.months })),
        },
        allocation: { equity: 0, debt: 100, gold: 0 },
        allocationNote:
          "Emergency money must be available quickly and should not fall in value when you need it. Keep about a month in your savings account and the rest in liquid or short-duration debt options, not in equity.",
        summary: `Emergency fund ${fmtINR(e.target)} (${v.months} months): ${fmtINR(e.gap)} to build`,
      };
    },
    ebook: {
      title: "The Emergency Fund Checklist",
      subtitle: "Build the cushion that keeps your plan on track",
      file: "/ebooks/emergency-fund-checklist.pdf",
      cover: "/ebooks/emergency-fund-checklist.png",
      chapters: [
        "Why every plan starts with an emergency fund",
        "How many months of cover do you need?",
        "Where to keep it: access, safety, a little growth",
        "Building it fast without stopping your SIPs",
        "Insurance: the other half of your safety net",
        "Your emergency-fund checklist",
      ],
    },
    faq: [
      { q: "How big should my emergency fund be?", a: "Commonly 6 months of essential expenses including EMIs. Consider more if your income is irregular, you are self-employed, or you have dependants." },
      { q: "Where should I keep it?", a: "In options you can access quickly with low risk of loss: a savings account for about a month's needs and liquid or short-duration debt funds for the rest." },
      { q: "Should I stop my SIPs to build it?", a: "Not necessarily. Many people split new savings between the emergency fund and existing SIPs until the fund is built." },
      { q: "Is an emergency fund the same as insurance?", a: "No. Health and term insurance cover large, specific risks; the emergency fund covers everyday shocks and the gap until insurance pays out." },
      { q: "Do liquid funds guarantee my money?", a: "No. Liquid and debt funds carry low but real risk. All figures here are illustrative." },
    ],
  },
  {
    slug: "child-marriage",
    name: "Child's Marriage",
    audience: "individual",
    seo: {
      title: "Child Marriage Planning Calculator & Free Guide | Rao Investment Solutions",
      description:
        "Estimate the future cost of your child's wedding and the monthly SIP to fund it without loans. Download the free guide, Planning a Wedding Without Debt.",
    },
    hero: {
      label: "Goal · Child's Marriage",
      title: "A celebration to remember,",
      accent: "not a loan to repay.",
      intro:
        "Weddings are joyful and expensive. Plan early, and the celebration can be funded from savings instead of loans or by breaking your retirement money.",
    },
    inputs: [
      { key: "cost", label: "Wedding cost today", min: 500000, max: 20000000, step: 100000, default: 2500000, format: fmtCompact },
      { key: "years", label: "Years until the wedding", min: 1, max: 25, step: 1, default: 15, format: yrs },
      { key: "existing", label: "Saved for this so far", min: 0, max: 10000000, step: 50000, default: 0, format: fmtCompact },
      { key: "inflation", label: "Inflation", min: 3, max: 10, step: 0.5, default: DEFAULTS.inflation, format: pct },
      { key: "ret", label: "Assumed annual return", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      const g = goalPlan({ cost: v.cost, years: v.years, inflation: v.inflation, annualReturn: v.ret, existing: v.existing });
      return {
        yearsLeft: v.years,
        headline: { label: "Estimated monthly SIP needed", value: fmtINR(g.sip) },
        stats: [
          { label: "Cost when the day comes", value: fmtCompact(g.futureCost) },
          { label: "Your savings may grow to", value: fmtCompact(g.fvExisting) },
          { label: "Gap to fund", value: fmtCompact(g.gap) },
          { label: "Or start a 10% step-up SIP at", value: fmtINR(g.stepSip) },
        ],
        series: g.series,
        delay: costOfDelay({ target: g.gap, years: v.years, annualReturn: v.ret }),
        summary: `Wedding in ${v.years} yrs: cost ${fmtCompact(g.futureCost)}, SIP ${fmtINR(g.sip)}/month`,
      };
    },
    ebook: {
      title: "Planning a Wedding Without Debt",
      subtitle: "How to fund your child's marriage and protect your own future",
      file: "/ebooks/wedding-without-debt.pdf",
      cover: "/ebooks/wedding-without-debt.png",
      chapters: [
        "What weddings may cost in 10–20 years",
        "Keeping the wedding fund separate",
        "Building the fund: SIP, step-up and gold",
        "De-risking as the date approaches",
        "Why loans and retirement withdrawals hurt",
        "Your wedding-fund checklist",
      ],
    },
    faq: [
      { q: "When should I start saving for my child's wedding?", a: "As early as possible, ideally alongside the education goal. With 15 years to go, a modest SIP can do much of the work." },
      { q: "Should I buy physical gold for the wedding?", a: "Many families do. Gold ETFs or gold funds let you build the allocation gradually without storage worries; jewellery can be bought closer to the date." },
      { q: "Should this be separate from the education fund?", a: "Yes. Separate goals with separate dates help you de-risk each one at the right time." },
      { q: "Is it okay to use my retirement savings?", a: "It is best avoided. Your child can borrow for many things; you cannot borrow for retirement." },
      { q: "Are these returns guaranteed?", a: "No. All figures are illustrative estimates. Mutual fund investments are subject to market risks." },
    ],
  },
  {
    slug: "women-wealth",
    name: "Women & Wealth",
    audience: "individual",
    seo: {
      title: "Financial Independence Planner for Women | Rao Investment Solutions",
      description:
        "Plan your own financial independence fund and the monthly SIP to build it. Download the free guide, Financial Independence for Women.",
    },
    hero: {
      label: "Goal · Women & Wealth",
      title: "Your money,",
      accent: "your independence.",
      intro:
        "Longer life expectancy, career breaks and caregiving years mean women often need more saved, with fewer working years to save it. Plan a fund that is yours, on your terms.",
    },
    inputs: [
      { key: "expense", label: "Your monthly expenses today", min: 10000, max: 400000, step: 5000, default: 50000, format: fmtINR },
      { key: "years", label: "Years to financial independence", min: 5, max: 35, step: 1, default: 20, format: yrs },
      { key: "existing", label: "Investments in your name", min: 0, max: 20000000, step: 50000, default: 300000, format: fmtCompact },
      { key: "inflation", label: "Inflation", min: 3, max: 10, step: 0.5, default: DEFAULTS.inflation, format: pct },
      { key: "ret", label: "Assumed annual return", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      // Independence fund: 25 years of today's expenses, a common rule of thumb.
      const g = goalPlan({ cost: v.expense * 12 * 25, years: v.years, inflation: v.inflation, annualReturn: v.ret, existing: v.existing });
      const breakSip = g.gap / sipFutureValue(1, v.ret, Math.max(1, v.years - 2)).fv;
      return {
        yearsLeft: v.years,
        headline: { label: "Estimated monthly SIP needed", value: fmtINR(g.sip) },
        stats: [
          { label: "Independence fund needed", value: fmtCompact(g.futureCost) },
          { label: "Your investments may grow to", value: fmtCompact(g.fvExisting) },
          { label: "Or start a 10% step-up SIP at", value: fmtINR(g.stepSip) },
          { label: "SIP if you plan a 2-yr break", value: fmtINR(breakSip) },
        ],
        series: g.series,
        delay: costOfDelay({ target: g.gap, years: v.years, annualReturn: v.ret }),
        summary: `Independence in ${v.years} yrs: fund ${fmtCompact(g.futureCost)}, SIP ${fmtINR(g.sip)}/month`,
      };
    },
    ebook: {
      title: "Financial Independence for Women",
      subtitle: "Build wealth in your own name, through every stage of life",
      file: "/ebooks/financial-independence-women.pdf",
      cover: "/ebooks/financial-independence-women.png",
      chapters: [
        "Why women need to plan differently",
        "Investments in your own name",
        "Planning around career breaks",
        "Your independence fund: how much is enough?",
        "Insurance, nominations and paperwork",
        "Your independence checklist",
      ],
    },
    faq: [
      { q: "Why should women plan separately?", a: "On average women live longer, may take career breaks and often earn less over a lifetime. Planning in your own name protects your independence in every life stage." },
      { q: "How do I keep investing during a career break?", a: "Build a buffer before the break, reduce rather than stop SIPs if you can, and step them up again on return." },
      { q: "What is a financial independence fund?", a: "A corpus large enough that your investments could cover your living costs. We use 25 years of expenses as a simple, illustrative starting point." },
      { q: "Should my investments be joint or in my name?", a: "Holding some investments in your own name, with correct nominations, gives you control and clarity in every situation." },
      { q: "Are these returns guaranteed?", a: "No. All figures are illustrative estimates. Mutual fund investments are subject to market risks." },
    ],
  },
  {
    slug: "nri-investing",
    name: "NRI Investing",
    audience: "individual",
    seo: {
      title: "NRI Goal Planner: Invest in India from Abroad | Rao Investment Solutions",
      description:
        "Plan an India-based goal from abroad: see the monthly investment in rupees and in your local currency. Download the free NRI guide.",
    },
    hero: {
      label: "Goal · NRI Investing",
      title: "Earn abroad,",
      accent: "build wealth at home.",
      intro:
        "A home in India, parents' care, or a return-home fund: plan your rupee goals from wherever you live, and see what to invest each month in your own currency.",
    },
    inputs: [
      { key: "cost", label: "Goal in India, today's cost", min: 1000000, max: 50000000, step: 500000, default: 5000000, format: fmtCompact },
      { key: "years", label: "Years to goal", min: 2, max: 25, step: 1, default: 10, format: yrs },
      { key: "existing", label: "Already invested in India", min: 0, max: 20000000, step: 100000, default: 0, format: fmtCompact },
      { key: "fx", label: "₹ per unit of your currency", min: 10, max: 120, step: 1, default: 84, format: (x) => `₹${x}` },
      { key: "inflation", label: "India inflation", min: 3, max: 10, step: 0.5, default: DEFAULTS.inflation, format: pct },
      { key: "ret", label: "Assumed annual return (₹)", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      const g = goalPlan({ cost: v.cost, years: v.years, inflation: v.inflation, annualReturn: v.ret, existing: v.existing });
      const local = (n) => Math.round(n / v.fx).toLocaleString("en-IN");
      return {
        yearsLeft: v.years,
        headline: { label: "Estimated monthly SIP needed (₹)", value: fmtINR(g.sip) },
        stats: [
          { label: "In your currency, per month", value: local(g.sip) },
          { label: "Goal cost when you need it", value: fmtCompact(g.futureCost) },
          { label: "Your India savings may grow to", value: fmtCompact(g.fvExisting) },
          { label: "Or start a 10% step-up SIP at", value: fmtINR(g.stepSip) },
        ],
        series: g.series,
        delay: costOfDelay({ target: g.gap, years: v.years, annualReturn: v.ret }),
        summary: `NRI goal ${fmtCompact(g.futureCost)} in ${v.years} yrs: SIP ${fmtINR(g.sip)}/mo (~${local(g.sip)} at ₹${v.fx})`,
      };
    },
    ebook: {
      title: "The NRI Guide to Investing in India",
      subtitle: "Accounts, KYC, tax and goal planning from abroad",
      file: "/ebooks/nri-guide-investing-india.pdf",
      cover: "/ebooks/nri-guide-investing-india.png",
      chapters: [
        "NRE vs NRO accounts: which money goes where",
        "KYC and FATCA for NRIs",
        "Mutual funds for NRIs, and country restrictions",
        "Planning rupee goals with foreign income",
        "Tax: TDS, DTAA and repatriation basics",
        "Your NRI investing checklist",
      ],
    },
    faq: [
      { q: "Can NRIs invest in Indian mutual funds?", a: "Yes, through NRE or NRO accounts after completing KYC. Some fund houses restrict investors resident in the US or Canada; we check which schemes are open to you." },
      { q: "NRE or NRO — which account should I use?", a: "NRE is for money earned abroad and is generally repatriable; NRO is for income earned in India. The right choice depends on the source of funds." },
      { q: "How are NRI mutual fund gains taxed?", a: "Tax is deducted at source on redemptions, and treaty (DTAA) benefits may apply depending on your country. Consult a tax adviser for your situation." },
      { q: "How does the exchange rate affect my goal?", a: "If your goal is in rupees, a weaker rupee makes it cheaper in your currency and a stronger one makes it dearer. We plan the goal in rupees and review the currency effect each year." },
      { q: "Are these returns guaranteed?", a: "No. All figures, including currency conversions, are illustrative estimates. Mutual fund investments are subject to market risks." },
    ],
  },
  {
    slug: "business-treasury",
    name: "Treasury & Surplus Funds",
    audience: "business",
    seo: {
      title: "Business Treasury Calculator: Put Idle Cash to Work | Rao Investment Solutions",
      description:
        "See what your company's idle balances could earn in liquid and short-term debt options. Download the free guide, Making Business Cash Work.",
    },
    hero: {
      label: "Business · Treasury",
      title: "Idle cash is",
      accent: "a silent cost.",
      intro:
        "Money sitting in a current account earns nothing. Matched to when you need it back, surplus funds can earn a return while staying available for payroll, vendors and growth.",
    },
    inputs: [
      { key: "balance", label: "Average idle balance", min: 1000000, max: 500000000, step: 1000000, default: 5000000, format: fmtCompact },
      { key: "current", label: "What it earns now", min: 0, max: 7, step: 0.25, default: 0, format: pct },
      { key: "liquid", label: "Liquid fund (assumed)", min: 4, max: 8, step: 0.25, default: 6.5, format: pct },
      { key: "short", label: "Short-duration debt (assumed)", min: 5, max: 9, step: 0.25, default: DEFAULTS.debtReturn, format: pct },
      { key: "years", label: "Years", min: 1, max: 10, step: 1, default: 3, format: yrs },
    ],
    compute: (v) => {
      const at = (r) => idleCashEarnings({ balance: v.balance, ratePct: r, years: v.years });
      const now = at(v.current);
      const liq = at(v.liquid);
      const sd = at(v.short);
      const series = [];
      for (let y = 1; y <= v.years; y++) {
        series.push({ year: y, value: Math.round(v.balance * Math.pow(1 + v.liquid / 100, y)), invested: Math.round(v.balance * Math.pow(1 + v.current / 100, y)) });
      }
      return {
        yearsLeft: 0,
        headline: { label: "Estimated extra earnings per year (liquid fund)", value: fmtINR(liq.yearly - now.yearly) },
        stats: [
          { label: "Earns now, per year", value: fmtINR(now.yearly) },
          { label: "Liquid fund, per year", value: fmtINR(liq.yearly) },
          { label: "Short-duration debt, per year", value: fmtINR(sd.yearly) },
          { label: `Extra over ${v.years} yrs (liquid)`, value: fmtCompact(liq.afterYears - now.afterYears) },
        ],
        series,
        chartLabels: { title: "Idle balance: now vs liquid fund", value: "In a liquid fund", invested: "As it is now" },
        compare: {
          title: "Estimated earnings per year",
          intro: `On ${fmtCompact(v.balance)}, before tax, at the assumed rates.`,
          rows: [
            { label: "As it is now", amount: now.yearly, display: fmtINR(now.yearly) },
            { label: "Liquid fund", amount: liq.yearly, highlight: true },
            { label: "Short-duration debt", amount: sd.yearly },
          ],
        },
        allocation: { equity: 0, debt: 100, gold: 0 },
        allocationNote:
          "Business surplus is matched to when it will be needed: operating cash in liquid funds (usually available the next working day), reserves for 1–3 years in short-duration debt or rated bonds. Equity is generally not used for working capital.",
        summary: `Treasury ${fmtCompact(v.balance)}: liquid fund may earn ${fmtINR(liq.yearly)}/yr vs ${fmtINR(now.yearly)} now`,
      };
    },
    ebook: {
      title: "Making Business Cash Work",
      subtitle: "A treasury guide for owners and finance heads",
      file: "/ebooks/making-business-cash-work.pdf",
      cover: "/ebooks/making-business-cash-work.png",
      chapters: [
        "The hidden cost of idle balances",
        "Mapping cash by when you will need it",
        "Liquid, overnight and short-duration funds",
        "Corporate bonds for reserves",
        "Approvals, tax and accounting basics",
        "Your treasury checklist",
      ],
    },
    faq: [
      { q: "Can a company invest in mutual funds?", a: "Yes. Companies, LLPs and partnership firms can invest with the right KYC and, for companies, a board resolution authorising the investment." },
      { q: "How quickly can we get money back from a liquid fund?", a: "Redemptions are usually credited the next working day. Overnight funds suit very short-term parking." },
      { q: "Is this safer than a bank FD?", a: "Different, not necessarily safer. Liquid and debt funds carry credit and interest-rate risk, which is low for high-quality short-term portfolios but not zero." },
      { q: "How are company gains on debt funds taxed?", a: "Gains are taxed as per applicable corporate tax rules. Speak to your chartered accountant for the treatment in your case." },
      { q: "Are these returns guaranteed?", a: "No. Liquid and debt fund returns vary with interest rates. All figures here are illustrative estimates before tax." },
    ],
  },
  {
    slug: "promoter-wealth",
    name: "Promoter Wealth",
    audience: "business",
    seo: {
      title: "Promoter Wealth Planner: From Profits to Family Wealth | Rao Investment Solutions",
      description:
        "See how regularly moving business profits into a diversified personal portfolio could build family wealth outside the company. Download the free guide.",
    },
    hero: {
      label: "Business · Promoter Wealth",
      title: "Your business is your engine.",
      accent: "It shouldn't be your only asset.",
      intro:
        "Most promoters have nearly all their net worth tied up in one company. Moving a part of profits into a diversified personal portfolio, year after year, builds family wealth that does not depend on any single business.",
    },
    inputs: [
      { key: "surplus", label: "Profits you can invest each year", min: 200000, max: 50000000, step: 100000, default: 2000000, format: fmtCompact },
      { key: "years", label: "Years", min: 3, max: 25, step: 1, default: 10, format: yrs },
      { key: "existing", label: "Personal investments today", min: 0, max: 100000000, step: 500000, default: 2500000, format: fmtCompact },
      { key: "business", label: "Value of your stake in the business", min: 1000000, max: 1000000000, step: 1000000, default: 50000000, format: fmtCompact },
      { key: "ret", label: "Assumed annual return", min: 6, max: 14, step: 0.5, default: DEFAULTS.equityReturn, format: pct },
    ],
    compute: (v) => {
      const sip = sipFutureValue(v.surplus / 12, v.ret, v.years);
      const fvExisting = v.existing * Math.pow(1 + v.ret / 100, v.years);
      const portfolio = sip.fv + fvExisting;
      const shareNow = (v.existing / (v.existing + v.business)) * 100;
      const shareLater = (portfolio / (portfolio + v.business)) * 100;
      const series = [];
      for (let y = 1; y <= v.years; y++) {
        const s = sipFutureValue(v.surplus / 12, v.ret, y);
        series.push({ year: y, value: Math.round(s.fv + v.existing * Math.pow(1 + v.ret / 100, y)), invested: Math.round(s.invested + v.existing) });
      }
      return {
        yearsLeft: v.years,
        headline: { label: `Personal portfolio may grow to, in ${v.years} yrs`, value: fmtCompact(portfolio) },
        stats: [
          { label: "Total moved out of the business", value: fmtCompact(sip.invested) },
          { label: "Net worth outside the business, now", value: `${shareNow.toFixed(0)}%` },
          { label: `Outside the business, in ${v.years} yrs*`, value: `${shareLater.toFixed(0)}%` },
          { label: "Monthly equivalent investment", value: fmtINR(v.surplus / 12) },
        ],
        series,
        compare: {
          title: "Where your net worth sits",
          intro: "*Assumes the business stake stays at today's value, to show diversification only.",
          rows: [
            { label: "Business stake", amount: v.business, display: fmtCompact(v.business) },
            { label: "Personal portfolio today", amount: v.existing, display: fmtCompact(v.existing) },
            { label: `Personal portfolio in ${v.years} yrs`, amount: portfolio, display: fmtCompact(portfolio), highlight: true },
          ],
        },
        summary: `Promoter: ${fmtCompact(v.surplus)}/yr for ${v.years} yrs → portfolio ${fmtCompact(portfolio)}, ${shareLater.toFixed(0)}% outside business`,
      };
    },
    ebook: {
      title: "From Business Profits to Family Wealth",
      subtitle: "A promoter's guide to building wealth outside the company",
      file: "/ebooks/business-profits-to-family-wealth.pdf",
      cover: "/ebooks/business-profits-to-family-wealth.png",
      chapters: [
        "The concentration problem",
        "How much to take out, and how",
        "Building a family portfolio: the asset mix",
        "Separating business and personal risk",
        "Succession, nominations and family structure",
        "Your promoter-wealth checklist",
      ],
    },
    faq: [
      { q: "How much of my profits should I move out of the business?", a: "It depends on the business's reinvestment needs. Many promoters set a fixed share of yearly profits to move into personal investments once the business is adequately funded." },
      { q: "Salary, dividend or drawings: which is better?", a: "Each is taxed differently. Your chartered accountant can advise on the most efficient route; we then put the money to work in a diversified plan." },
      { q: "Should my personal portfolio be in equity?", a: "Your business is already a large, concentrated risk. The personal portfolio is usually diversified across equity, debt and gold, often with more debt than a salaried investor would hold." },
      { q: "Can my family's portfolio be held separately?", a: "Yes. Investments can be structured across family members with correct nominations, and we can discuss succession planning with your advisers." },
      { q: "Are these returns guaranteed?", a: "No. All figures are illustrative estimates. Mutual fund investments are subject to market risks." },
    ],
  },
];

export const getGoal = (slug) => GOALS.find((g) => g.slug === slug);
